package org.teddapp.project.ui.focus

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.delay
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddConsigne
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/** Durée d'une pause active : soixante secondes d'effort, pas davantage. */
internal const val BDNF_SECONDS = 60

/** Exercices proposés. Aucun matériel, faisable là où on travaille. */
internal val BDNF_EXERCISES = listOf("Planche", "Montées de genoux", "Squats", "Pompes", "Corde à sauter")

/**
 * Pause active d'une minute (Risser) : un effort court stimule le BDNF et prépare la
 * mémorisation. Elle est facultative, proposée après une course de focus.
 *
 * Sans capteur de pouls, le score se déduit de la part de la minute réellement tenue.
 */
@Composable
internal fun BdnfPauseDialog(
    onDone: (exercise: String, seconds: Int, efficiency: Double) -> Unit,
    onDismiss: () -> Unit,
) {
    var exercise by remember { mutableStateOf(BDNF_EXERCISES.first()) }
    var started by remember { mutableStateOf(false) }
    var remaining by remember { mutableStateOf(BDNF_SECONDS) }

    LaunchedEffect(started) {
        if (!started) return@LaunchedEffect
        while (remaining > 0) {
            delay(1000L)
            remaining -= 1
        }
    }

    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        TeddConsigne(
            eyebrow = if (started) "Pause active · $exercise" else "Pause active · une minute",
            text = when {
                !started -> "Une minute d'effort maintenant, et ce que tu viens d'apprendre tiendra mieux."
                remaining > 0 -> "Tiens l'effort. Respire par le nez, sans bloquer."
                else -> "C'est fini. Laisse le souffle revenir avant de repartir."
            },
            modifier = Modifier.fillMaxSize(),
        ) {
            if (!started) {
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    BDNF_EXERCISES.forEach { e ->
                        val on = exercise == e
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(TeddTokens.RFull))
                                .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { exercise = e }
                                .padding(horizontal = 12.dp, vertical = 7.dp),
                        ) {
                            Text(e, color = if (on) TeddTokens.OnAction else TeddTokens.Ink, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
                TeddButton(
                    text = "Commencer",
                    onClick = { started = true },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Lg,
                    modifier = Modifier.fillMaxWidth(),
                )
                TeddButton(text = "Pas maintenant", onClick = onDismiss, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
            } else {
                Box(
                    modifier = Modifier
                        .size(150.dp)
                        .clip(CircleShape)
                        .background(if (remaining > 0) TeddTokens.LimeSoft else TeddTokens.SuccessSoft, CircleShape),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = if (remaining > 0) "$remaining" else "✓",
                        style = TeddType.Numeral.copy(fontSize = 44.sp, lineHeight = 50.sp),
                        color = if (remaining > 0) TeddTokens.LimeStrong else TeddTokens.Success,
                    )
                }
                if (remaining > 0) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        TeddButton(
                            text = "J'arrête là",
                            onClick = {
                                val done = BDNF_SECONDS - remaining
                                onDone(exercise, done, done.toDouble() / BDNF_SECONDS)
                            },
                            kind = TeddButtonKind.Secondary,
                            size = TeddButtonSize.Md,
                        )
                    }
                } else {
                    TeddButton(
                        text = "Enregistrer la pause",
                        onClick = { onDone(exercise, BDNF_SECONDS, 1.0) },
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Lg,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
    }
}
